export class OptimalDeviceId {
    constructor() {
        this.STORAGE_KEY = 'fcm_device_id';
        this.FINGERPRINT_KEY = 'device_fingerprint';
    }

    // MAIN METHOD: Get the most reliable device ID
    async getDeviceId() {
        try {
            // Step 1: Try to get existing persistent ID
            let deviceId = await this.getPersistentId();

            // Step 2: Generate current fingerprint
            const currentFingerprint = await this.generateLightweightFingerprint();

            // Step 3: Validate device consistency
            const validation = await this.validateDevice(deviceId, currentFingerprint);

            if (!deviceId || validation.shouldRegenerate) {
                // Generate new device ID
                deviceId = this.generateNewDeviceId();
                await this.savePersistentId(deviceId);
                console.log('Generated new device ID:', deviceId);
            }

            // Step 4: Update fingerprint
            await this.saveFingerprint(currentFingerprint);

            return deviceId;

        } catch (error) {
            console.error('Error getting device ID:', error);
            // Fallback to simple random ID
            return this.generateFallbackId();
        }
    }

    // Lightweight fingerprint - fast and reliable
    async generateLightweightFingerprint() {
        const components = {
            // Screen (most stable)
            //   screen: `${screen.width}x${screen.height}x${screen.colorDepth}`,
            screen: `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`,
            // Timezone (fairly stable)
            tz: new Date().getTimezoneOffset(),

            // Language (stable)
            lang: navigator.language,

            // Platform (stable)
            platform: navigator.platform,

            // Hardware (when available, very stable)
            hw: (navigator.hardwareConcurrency || 0) + '_' + (navigator.deviceMemory || 0),

            // Browser engine (semi-stable)
            engine: this.getBrowserEngine()
        };

        const fingerprintString = JSON.stringify(components);
        const hash = await this.simpleHash(fingerprintString);

        return hash.substring(0, 12); // Short but unique enough
    }

    getBrowserEngine() {
        const ua = navigator.userAgent;
        if (ua.includes('Chrome')) return 'blink';
        if (ua.includes('Firefox')) return 'gecko';
        if (ua.includes('Safari') && !ua.includes('Chrome')) return 'webkit';
        if (ua.includes('Edge')) return 'blink';
        return 'unknown';
    }

    async simpleHash(str) {
        if (crypto.subtle) {
            const encoder = new TextEncoder();
            const data = encoder.encode(str);
            const hashBuffer = await crypto.subtle.digest('SHA-256', data);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        } else {
            // Simple fallback hash
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
                hash = ((hash << 5) - hash) + str.charCodeAt(i);
                hash = hash & hash;
            }
            return Math.abs(hash).toString(16);
        }
    }

    generateNewDeviceId() {
        const timestamp = Date.now().toString(36);
        const random = Math.random().toString(36).substring(2, 8);
        const browser = this.getBrowserEngine().substring(0, 2);

        return `web_${browser}_${timestamp}_${random}`;
    }

    generateFallbackId() {
        return `fallback_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
    }

    getBrowserInfo() {
        const userAgent = navigator.userAgent;
        const browserInfo = {
            name: 'Unknown',
            version: 'Unknown',
            engine: 'Unknown'
        };

        if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
            browserInfo.name = 'Chrome';
            const match = userAgent.match(/Chrome\/(\d+\.\d+)/);
            browserInfo.version = match ? match[1] : 'Unknown';
            browserInfo.engine = 'Blink';
        }
        else if (userAgent.includes('Edg')) {
            browserInfo.name = 'Edge';
            const match = userAgent.match(/Edg\/(\d+\.\d+)/);
            browserInfo.version = match ? match[1] : 'Unknown';
            browserInfo.engine = 'Blink';
        }
        else if (userAgent.includes('Firefox')) {
            browserInfo.name = 'Firefox';
            const match = userAgent.match(/Firefox\/(\d+\.\d+)/);
            browserInfo.version = match ? match[1] : 'Unknown';
            browserInfo.engine = 'Gecko';
        }
        else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
            browserInfo.name = 'Safari';
            const match = userAgent.match(/Version\/(\d+\.\d+)/);
            browserInfo.version = match ? match[1] : 'Unknown';
            browserInfo.engine = 'WebKit';
        }
        else if (userAgent.includes('OPR')) {
            browserInfo.name = 'Opera';
            const match = userAgent.match(/OPR\/(\d+\.\d+)/);
            browserInfo.version = match ? match[1] : 'Unknown';
            browserInfo.engine = 'Blink';
        }

        return browserInfo;
    }

    getOSInfo() {
        const userAgent = navigator.userAgent;

        if (userAgent.includes('Windows NT')) {
            const match = userAgent.match(/Windows NT (\d+\.\d+)/);
            const version = match ? match[1] : '';
            const versionMap = {
                '10.0': '10/11',
                '6.3': '8.1',
                '6.2': '8',
                '6.1': '7'
            };
            return `Windows ${versionMap[version] || version}`;
        }
        else if (userAgent.includes('Mac OS X')) {
            const match = userAgent.match(/Mac OS X (\d+[._]\d+)/);
            const version = match ? match[1].replace('_', '.') : '';
            return `macOS ${version}`;
        }
        else if (userAgent.includes('Linux')) {
            return 'Linux';
        }
        else if (userAgent.includes('Android')) {
            const match = userAgent.match(/Android (\d+\.\d+)/);
            return `Android ${match ? match[1] : ''}`;
        }
        else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
            const match = userAgent.match(/OS (\d+_\d+)/);
            const version = match ? match[1].replace('_', '.') : '';
            return `iOS ${version}`;
        }

        return navigator.platform || 'Unknown';
    }

    getFullDeviceInfo() {
        const browserInfo = this.getBrowserInfo();
        const osInfo = this.getOSInfo();

        return {
            browser_name: browserInfo.name,
            browser_version: browserInfo.version,
            browser_engine: browserInfo.engine,
            os_name: osInfo,
            screen: `${window.screen.width}x${window.screen.height}`,
            language: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            hardware_concurrency: navigator.hardwareConcurrency || 0,
            device_memory: navigator.deviceMemory || 0
        };
    }

    // Multi-layer persistent storage
    async getPersistentId() {
        // Priority order: localStorage -> IndexedDB -> Cookie

        // Try localStorage first (fastest)
        try {
            const id = localStorage.getItem(this.STORAGE_KEY);
            if (id) return id;
        } catch (e) { }

        // Try IndexedDB (survives private browsing better)
        try {
            const id = await this.getFromIndexedDB();
            if (id) {
                // Sync back to localStorage for speed
                try { localStorage.setItem(this.STORAGE_KEY, id); } catch (e) { }
                return id;
            }
        } catch (e) { }

        // Try cookie as last resort
        try {
            const id = this.getCookie(this.STORAGE_KEY);
            if (id) {
                // Sync back to other storage
                try { localStorage.setItem(this.STORAGE_KEY, id); } catch (e) { }
                try { await this.saveToIndexedDB(id); } catch (e) { }
                return id;
            }
        } catch (e) { }

        return null;
    }

    async savePersistentId(deviceId) {
        const promises = [];

        // Save to localStorage
        promises.push(this.saveToStorage(() => {
            localStorage.setItem(this.STORAGE_KEY, deviceId);
        }));

        // Save to IndexedDB
        promises.push(this.saveToIndexedDB(deviceId));

        // Save to cookie (1 year)
        promises.push(this.saveToStorage(() => {
            this.setCookie(this.STORAGE_KEY, deviceId, 365);
        }));

        // Don't wait for all to complete, but try all
        Promise.allSettled(promises);
    }

    async saveToStorage(fn) {
        try {
            fn();
            return true;
        } catch (error) {
            return false;
        }
    }

    async validateDevice(deviceId, currentFingerprint) {
        if (!deviceId) {
            return { shouldRegenerate: true, reason: 'no_device_id' };
        }

        try {
            const storedFingerprint = localStorage.getItem(this.FINGERPRINT_KEY);

            if (!storedFingerprint) {
                return { shouldRegenerate: false, reason: 'first_time' };
            }

            // Compare fingerprints - allow some tolerance for minor changes
            const similarity = this.calculateSimilarity(storedFingerprint, currentFingerprint);

            if (similarity < 0.7) { // 70% similarity threshold
                console.warn('Device fingerprint changed significantly', {
                    stored: storedFingerprint,
                    current: currentFingerprint,
                    similarity
                });
                return {
                    shouldRegenerate: true,
                    reason: 'fingerprint_mismatch',
                    similarity
                };
            }

            return {
                shouldRegenerate: false,
                reason: 'valid',
                similarity
            };

        } catch (error) {
            return { shouldRegenerate: false, reason: 'validation_error' };
        }
    }

    calculateSimilarity(str1, str2) {
        if (str1 === str2) return 1.0;

        const longer = str1.length > str2.length ? str1 : str2;
        const shorter = str1.length > str2.length ? str2 : str1;

        if (longer.length === 0) return 1.0;

        // Simple character overlap calculation
        let matches = 0;
        for (let i = 0; i < shorter.length; i++) {
            if (longer.includes(shorter[i])) matches++;
        }

        return matches / longer.length;
    }

    async saveFingerprint(fingerprint) {
        try {
            localStorage.setItem(this.FINGERPRINT_KEY, fingerprint);
        } catch (error) {
            // Fingerprint storage is not critical
        }
    }

    // IndexedDB helpers
    async getFromIndexedDB() {
        return new Promise((resolve) => {
            try {
                const request = indexedDB.open('FCMDeviceStorage', 1);

                request.onupgradeneeded = (e) => {
                    const db = e.target.result;
                    if (!db.objectStoreNames.contains('device')) {
                        db.createObjectStore('device');
                    }
                };

                request.onsuccess = (e) => {
                    const db = e.target.result;
                    const transaction = db.transaction(['device'], 'readonly');
                    const store = transaction.objectStore('device');
                    const getRequest = store.get(this.STORAGE_KEY);

                    getRequest.onsuccess = () => resolve(getRequest.result || null);
                    getRequest.onerror = () => resolve(null);
                };

                request.onerror = () => resolve(null);

                // Timeout fallback
                setTimeout(() => resolve(null), 1000);
            } catch (error) {
                resolve(null);
            }
        });
    }

    async saveToIndexedDB(deviceId) {
        return new Promise((resolve) => {
            try {
                const request = indexedDB.open('FCMDeviceStorage', 1);

                request.onupgradeneeded = (e) => {
                    const db = e.target.result;
                    if (!db.objectStoreNames.contains('device')) {
                        db.createObjectStore('device');
                    }
                };

                request.onsuccess = (e) => {
                    const db = e.target.result;
                    const transaction = db.transaction(['device'], 'readwrite');
                    const store = transaction.objectStore('device');
                    store.put(deviceId, this.STORAGE_KEY);
                    resolve(true);
                };

                request.onerror = () => resolve(false);

                // Timeout fallback
                setTimeout(() => resolve(false), 1000);
            } catch (error) {
                resolve(false);
            }
        });
    }

    // Cookie helpers
    getCookie(name) {
        try {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) {
                return parts.pop().split(';').shift();
            }
        } catch (error) { }
        return null;
    }

    setCookie(name, value, days) {
        try {
            const expires = new Date(Date.now() + days * 864e5).toUTCString();
            document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax; Secure`;
        } catch (error) {
            // Cookie setting failed - not critical
        }
    }

    // Debug info
    async getDebugInfo() {
        const deviceId = await this.getDeviceId();
        const fingerprint = await this.generateLightweightFingerprint();

        return {
            deviceId,
            fingerprint,
            storageSupport: {
                localStorage: !!window.localStorage,
                indexedDB: !!window.indexedDB,
                cookies: navigator.cookieEnabled
            },
            browser: this.getBrowserEngine(),
            screen: `${window.screen.width}x${window.screen.height}`
        };
    }
}